import { ChangeEvent, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell, Toolbar, TableButton } from '@/shared/components/MaterialTableElements';
import PlusIcon from 'mdi-react/PlusIcon';
import { LoadingSpinner } from '@/shared/components/Loading';
import Course33Media from '@/models/course-33-media';
import { useCourse33Media } from '@/services/course-33-media';
import MediaModal, { MediaSectionMode } from './components/media-modal';

const SLOT_LABEL: Record<string, string> = { dayZeroVideo: 'Video', morning: 'Morning', night: 'Night' };

const COPY: Record<MediaSectionMode, { title: string; subhead: string; addLabel: string; empty: string }> = {
    day0video: {
        title: 'Course 33 · Day-0 Videos',
        subhead: 'The orientation videos shown on the Day 0 page (between “People who built lives on this” and “What today’s session needs”). Mux-streamed.',
        addLabel: 'Add Day-0 Video',
        empty: 'No Day-0 videos yet. Add one to get started.',
    },
    dayMedia: {
        title: 'Course 33 · Day Media',
        subhead: 'Per-day Morning (daily affirmation) and Night audio for days 1–21. Pick a day + slot, upload the audio (Mux-streamed). Leave a day empty to show “coming soon”.',
        addLabel: 'Add Day Media',
        empty: 'No day media yet. Add a morning or night audio to get started.',
    },
};

const Course33MediaSection = ({ mode }: { mode: MediaSectionMode }) => {
    const { data: media, isLoading } = useCourse33Media();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<Course33Media | undefined>();

    const copy = COPY[mode];

    const rows = useMemo(() => {
        const all = media?.data ?? [];
        const scoped = mode === 'day0video' ? all.filter((m) => m.slot === 'dayZeroVideo') : all.filter((m) => m.slot !== 'dayZeroVideo');
        return scoped
            .slice()
            .sort((a, b) => (mode === 'day0video' ? a.orderIndex - b.orderIndex : a.day - b.day || a.slot.localeCompare(b.slot) || a.orderIndex - b.orderIndex));
    }, [media, mode]);

    const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1));

    const toggle = () => setIsOpen((prev) => !prev);
    const handleAdd = () => {
        setSelectedItem(undefined);
        toggle();
    };
    const handleClick = (item: Course33Media) => {
        setSelectedItem(item);
        toggle();
    };
    const handleChangePage = (_: unknown, item: number) => setPage(item);
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    const colCount = mode === 'day0video' ? 3 : 5;

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">{copy.title}</h3>
                    <h3 className="page-subhead subhead">{copy.subhead}</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-end">
                                        <Toolbar className="material-table__toolbar">
                                            <div className="d-flex align-items-center justify-content-center" role="button" onClick={handleAdd}>
                                                <TableButton>
                                                    <PlusIcon />
                                                </TableButton>
                                                <span>{copy.addLabel}</span>
                                            </div>
                                        </Toolbar>
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    {mode === 'dayMedia' && <TableCell>Day</TableCell>}
                                                    {mode === 'dayMedia' && <TableCell>Slot</TableCell>}
                                                    {mode === 'day0video' && <TableCell>Order</TableCell>}
                                                    <TableCell>Title</TableCell>
                                                    <TableCell>Caption</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage).map((d) => (
                                                    <TableRow role="checkbox" onClick={() => handleClick(d)} tabIndex={-1} key={d.id}>
                                                        {mode === 'dayMedia' && <TableCell>Day {d.day}</TableCell>}
                                                        {mode === 'dayMedia' && <TableCell>{SLOT_LABEL[d.slot] ?? d.slot}</TableCell>}
                                                        {mode === 'day0video' && <TableCell>{d.orderIndex}</TableCell>}
                                                        <TableCell component="th" scope="row">
                                                            {d.title}
                                                        </TableCell>
                                                        <TableCell>{d.caption ?? '—'}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {rows.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={colCount} className="text-center">
                                                            {copy.empty}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={rows.length}
                                        rowsPerPage={rowsPerPage}
                                        page={safePage}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <MediaModal isOpen={isOpen} toggle={toggle} mode={mode} item={selectedItem} />
        </Container>
    );
};

export default Course33MediaSection;
